import {
    useState, useMemo, useRef, useEffect,
    ChangeEvent
} from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmModal, FileType, ManagedFile, PreviewModal, RenameModal, SortDir, SortField, ToastState, TypeFilter, ViewMode } from "./types";
import { ALL_FILES, ALL_FOLDERS, PAGE_SIZES, TYPE_CONFIG } from "./constants";
import { FileCard, FileThumbnail, fmtSize, formatDate, highlight, PreviewModalComp, RenameModalComp, TypeBadge } from "./helpers";
import DeleteModal from "../../components/common/Modal/DeleteModal";
import StorageBar from "../../components/pages/contents/StorageBar";
import { SortIcon } from "../../components/common/UI/Icons/SortIcon";
import { IconBtn } from "../../components/common/UI/Button/IconBtn";

export default function AllFiles() {
    const [files, setFiles] = useState<ManagedFile[]>(ALL_FILES);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
    const [folderFilter, setFolderFilter] = useState("All folders");
    const [sortField, setSortField] = useState<SortField>("createdAt");
    const [sortDir, setSortDir] = useState<SortDir>("desc");
    const [viewMode, setViewMode] = useState<ViewMode>("grid");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [confirmModal, setConfirmModal] = useState<ConfirmModal>({ open: false, id: null, name: "" });
    const [renameModal, setRenameModal] = useState<RenameModal>({ open: false, id: null, name: "" });
    const [previewModal, setPreviewModal] = useState<PreviewModal>({ open: false, file: null });
    const [toast, setToast] = useState<ToastState>({ visible: false, message: "", type: "success" });
    const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const navigate = useNavigate();

    const showToast = (message: string, type: ToastState["type"] = "success") => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setToast({ visible: true, message, type });
        toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
    };

    // ── Derived ──────────────────────────────────────────────────────────────

    const filtered = useMemo(() => {
        let list = [...files];
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter((f) => f.name.toLowerCase().includes(q) || f.folder.toLowerCase().includes(q) || f.id.includes(q));
        }
        if (typeFilter !== "all") list = list.filter((f) => f.type === typeFilter);
        if (folderFilter !== "All folders") list = list.filter((f) => f.folder === folderFilter);
        list.sort((a, b) => {
            let va: string | number = a[sortField] ?? "";
            let vb: string | number = b[sortField] ?? "";
            if (sortField === "size") { va = a.size; vb = b.size; }
            const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
            return sortDir === "asc" ? cmp : -cmp;
        });
        return list;
    }, [files, search, typeFilter, folderFilter, sortField, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageSlice = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

    useEffect(() => { setPage(1); }, [search, typeFilter, folderFilter, pageSize]);

    // ── Selection ────────────────────────────────────────────────────────────

    const allSelected = pageSlice.length > 0 && pageSlice.every((f) => selected.has(f.id));
    const someSelected = selected.size > 0;
    const toggleAll = () => {
        const next = new Set(selected);
        allSelected ? pageSlice.forEach((f) => next.delete(f.id)) : pageSlice.forEach((f) => next.add(f.id));
        setSelected(next);
    };
    const toggle = (id: string) => {
        const next = new Set(selected); next.has(id) ? next.delete(id) : next.add(id); setSelected(next);
    };

    // ── Actions ──────────────────────────────────────────────────────────────

    const toggleSort = (f: SortField) => {
        if (sortField === f) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortField(f); setSortDir("asc"); }
    };

    const handleDeleteConfirm = () => {
        if (!confirmModal.id) return;
        setFiles((prev) => prev.filter((f) => f.id !== confirmModal.id));
        setSelected((prev) => { const n = new Set(prev); n.delete(confirmModal.id!); return n; });
        setConfirmModal({ open: false, id: null, name: "" });
        showToast("File deleted");
    };

    const handleBulkDelete = () => {
        setFiles((prev) => prev.filter((f) => !selected.has(f.id)));
        showToast(`${selected.size} file${selected.size > 1 ? "s" : ""} deleted`);
        setSelected(new Set());
    };

    const handleRenameConfirm = (newName: string) => {
        if (!renameModal.id || !newName.trim()) return;
        setFiles((prev) => prev.map((f) => f.id === renameModal.id ? { ...f, name: newName.trim(), updatedAt: new Date().toISOString() } : f));
        setRenameModal({ open: false, id: null, name: "" });
        showToast("File renamed");
    };

    const handleCopyUrl = (file: ManagedFile) => {
        const url = file.url || `https://cdn.auralglobal.com/${file.folder}/${file.name}`;
        navigator.clipboard?.writeText(url).catch(() => { });
        showToast("URL copied to clipboard", "info");
    };

    // ── Pagination ────────────────────────────────────────────────────────────

    const pageNums = (): (number | "…")[] => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (safePage <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
        if (safePage >= totalPages - 3) return [1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        return [1, "…", safePage - 1, safePage, safePage + 1, "…", totalPages];
    };

    // ── Stats ─────────────────────────────────────────────────────────────────

    const stats = useMemo(() => {
        const totalSize = files.reduce((s, f) => s + f.size, 0);
        return {
            total: files.length,
            images: files.filter((f) => f.type === "image").length,
            docs: files.filter((f) => f.type === "document").length,
            size: fmtSize(totalSize),
            videos: files.filter((f) => f.type === "video").length,
        };
    }, [files]);

    const btnStyle = (active: boolean): React.CSSProperties => ({
        width: 30, height: 30, borderRadius: 7, cursor: "pointer", display: "flex",
        alignItems: "center", justifyContent: "center",
        border: `1px solid ${active ? "rgba(191,61,12,0.35)" : "var(--border)"}`,
        background: active ? "var(--or-dim)" : "transparent",
        color: active ? "var(--or)" : "var(--text2)",
        transition: "all 0.15s",
    });

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        // :root {
        //   --or:#BF3D0C; --or2:#E8593C; --or-dim:rgba(191,61,12,0.10);
        //   --text:#e8e6de; --text2:#b4b2a9; --text3:#6a6a65;
        //   --border:rgba(255,255,255,0.08); --card:rgba(255,255,255,0.025);
        //   --row-hover:rgba(255,255,255,0.028); --input-bg:rgba(255,255,255,0.035);
        //   --section-header:rgba(255,255,255,0.02); --surface:#111110;
        // }
        body { margin:0; font-family:'DM Sans',sans-serif; }
        input::placeholder, select { color:var(--text3); }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.08); border-radius:4px; }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastIn     { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes modalIn     { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
        .af-row { transition:background 0.12s; }
        .af-row:hover { background:var(--row-hover) !important; }
        .af-th { user-select:none; cursor:pointer; }
        .af-th:hover span { color:var(--text) !important; }
        .af-pg:hover:not(:disabled) { background:var(--or-dim)!important; color:var(--or)!important; border-color:rgba(191,61,12,0.3)!important; }
        .af-filter:hover { background:rgba(255,255,255,0.05)!important; }
        .af-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(190px,1fr)); gap:12px; }
        @media(max-width: 768px) {.stat-cards { grid-template-columns:repeat(2,1fr) !important; } .header-style {display:block !important} .button-style {margin-top:20px !important} }
        @media(max-width:900px){ .af-grid { grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); } }
      `}</style>

            {/* Toast */}
            {toast.visible && (
                <div style={{
                    position: "fixed", top: 20, right: 20, zIndex: 600,
                    background: toast.type === "success" ? "#1a2e1a" : toast.type === "info" ? "#1a1f2e" : "#2e1a1a",
                    border: `1px solid ${toast.type === "success" ? "rgba(90,200,90,0.25)" : toast.type === "info" ? "rgba(138,180,248,0.25)" : "rgba(224,82,82,0.25)"}`,
                    borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10,
                    animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                }}>
                    <span style={{ fontSize: 14, color: toast.type === "success" ? "#9be09b" : toast.type === "info" ? "#8ab4f8" : "#e08b8b" }}>
                        {toast.type === "success" ? "✓" : toast.type === "info" ? "⎘" : "✕"}&nbsp;&nbsp;{toast.message}
                    </span>
                </div>
            )}

            <DeleteModal modal={confirmModal}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirmModal({ open: false, id: null, name: "" })} />
            <RenameModalComp modal={renameModal}
                onConfirm={handleRenameConfirm}
                onCancel={() => setRenameModal({ open: false, id: null, name: "" })} />
            <PreviewModalComp modal={previewModal} onClose={() => setPreviewModal({ open: false, file: null })} />

            <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
                <div style={{ maxWidth: 1280, margin: "0 auto", animation: "fadeSlideIn 0.3s ease" }}>

                    {/* ── Header ── */}
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }} className="header-style">
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>Assets</div>
                            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>File Manager</h1>
                            <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text3)" }}>
                                {files.length} files · {stats.size} used
                            </p>
                        </div>
                        <button
                            style={{
                                display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 9,
                                border: "1px solid rgba(191,61,12,0.35)", background: "linear-gradient(135deg,#BF3D0C,#E8593C)",
                                color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer",
                                fontFamily: "'DM Sans',sans-serif", boxShadow: "0 2px 12px rgba(191,61,12,0.25)",
                            }}
                            className="button-style"
                            onClick={() => navigate('/content/upload')}
                        >
                            <span style={{ fontSize: 18, lineHeight: 1 }}>↑</span> Upload Files
                        </button>
                    </div>

                    {/* ── Stat cards ── */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 }} className="stat-cards">
                        {[
                            { label: "Total Files", value: stats.total, color: "var(--text2)", sub: "all types" },
                            { label: "Images", value: stats.images, color: "#6ddc6d", sub: "photos & graphics" },
                            { label: "Documents", value: stats.docs, color: "#8ab4f8", sub: "PDF, DOCX, etc." },
                            { label: "Videos", value: stats.videos, color: "#c678f5", sub: "MP4, MOV, etc." },
                            { label: "Total Size", value: stats.size, color: "#f5a623", sub: "of 1 GB quota" },
                        ].map((s) => (
                            <div key={s.label} style={{
                                background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 18px",
                            }}>
                                <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 4 }}>{s.label}</div>
                                <div style={{ fontSize: typeof s.value === "string" && s.value.length > 4 ? 18 : 22, fontWeight: 600, color: s.color, lineHeight: 1, marginBottom: 3 }}>{s.value}</div>
                                <div style={{ fontSize: 11, color: "var(--text3)" }}>{s.sub}</div>
                            </div>
                        ))}
                    </div>

                    {/* ── Storage bar ── */}
                    <div style={{ marginBottom: 16 }}>
                        <StorageBar files={files} />
                    </div>

                    {/* ── Main panel ── */}
                    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>

                        {/* Toolbar */}
                        <div style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "14px 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap",
                        }}>
                            {/* Search */}
                            <div style={{ position: "relative", flex: "1 1 200px", minWidth: 160 }}>
                                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--text3)", fontSize: 14, pointerEvents: "none" }}>⌕</span>
                                <input
                                    value={search}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                                    placeholder="Search files…"
                                    style={{
                                        width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)",
                                        borderRadius: 8, padding: "8px 30px 8px 32px", fontSize: 13, color: "var(--text)",
                                        outline: "none", fontFamily: "'DM Sans',sans-serif",
                                    }}
                                />
                                {search && (
                                    <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: 14 }}>×</button>
                                )}
                            </div>

                            {/* Type filter pills */}
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                {(["all", "image", "document", "video", "archive", "spreadsheet"] as (TypeFilter)[]).map((t) => {
                                    const active = typeFilter === t;
                                    const cfg = t !== "all" ? TYPE_CONFIG[t as FileType] : null;
                                    return (
                                        <button key={t} className="af-filter" onClick={() => setTypeFilter(t)} style={{
                                            fontSize: 12, padding: "5px 11px", borderRadius: 7, cursor: "pointer",
                                            border: `1px solid ${active ? (cfg ? cfg.border : "rgba(191,61,12,0.3)") : "var(--border)"}`,
                                            background: active ? (cfg ? cfg.bg : "var(--or-dim)") : "transparent",
                                            color: active ? (cfg ? cfg.color : "var(--or)") : "var(--text3)",
                                            fontFamily: "'DM Sans',sans-serif", textTransform: "capitalize", transition: "all 0.15s",
                                        }}>{t === "all" ? "All types" : t}</button>
                                    );
                                })}
                            </div>

                            {/* Folder filter */}
                            <select
                                value={folderFilter}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) => setFolderFilter(e.target.value)}
                                style={{
                                    fontSize: 13, padding: "7px 12px", borderRadius: 8,
                                    border: "1px solid var(--border)", background: "rgba(255,255,255,0.04)",
                                    color: folderFilter !== "All folders" ? "var(--or)" : "var(--text3)",
                                    cursor: "pointer", fontFamily: "'DM Sans',sans-serif", outline: "none",
                                }}
                            >
                                {ALL_FOLDERS.map((f) => (
                                    <option key={f} value={f} style={{ background: "#1a1917", color: "#e8e6de" }}>{f}</option>
                                ))}
                            </select>

                            {/* Bulk actions */}
                            {someSelected && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ fontSize: 12, color: "var(--text3)" }}>{selected.size} selected</span>
                                    <button onClick={handleBulkDelete} style={{
                                        fontSize: 12, padding: "6px 12px", borderRadius: 7, cursor: "pointer",
                                        border: "1px solid rgba(224,82,82,0.3)", background: "rgba(224,82,82,0.08)",
                                        color: "#e05252", fontFamily: "'DM Sans',sans-serif",
                                    }}>Delete selected</button>
                                </div>
                            )}

                            {/* View toggle */}
                            <div style={{ display: "flex", gap: 4, marginLeft: someSelected ? 0 : "auto" }}>
                                <button onClick={() => setViewMode("grid")} style={btnStyle(viewMode === "grid")} title="Grid view">
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
                                        <rect x="0" y="0" width="6" height="6" rx="1" /><rect x="10" y="0" width="6" height="6" rx="1" />
                                        <rect x="0" y="10" width="6" height="6" rx="1" /><rect x="10" y="10" width="6" height="6" rx="1" />
                                    </svg>
                                </button>
                                <button onClick={() => setViewMode("table")} style={btnStyle(viewMode === "table")} title="List view">
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <line x1="4" y1="4" x2="15" y2="4" /><line x1="4" y1="8" x2="15" y2="8" /><line x1="4" y1="12" x2="15" y2="12" />
                                        <circle cx="1.5" cy="4" r="1" /><circle cx="1.5" cy="8" r="1" /><circle cx="1.5" cy="12" r="1" />
                                    </svg>
                                </button>
                            </div>
                            <span style={{ fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>
                                {filtered.length} file{filtered.length !== 1 ? "s" : ""}
                            </span>
                        </div>

                        {/* ── Grid view ── */}
                        {viewMode === "grid" && (
                            <div style={{ padding: 16 }}>
                                {pageSlice.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: "56px 0", color: "var(--text3)" }}>
                                        <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.3 }}>📁</div>
                                        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text2)", marginBottom: 6 }}>
                                            {search ? `No files matching "${search}"` : "No files found"}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="af-grid">
                                        {pageSlice.map((file, i) => (
                                            <div key={file.id} style={{ animation: `fadeSlideIn 0.2s ease ${i * 0.025}s both` }}>
                                                <FileCard
                                                    file={file} selected={selected.has(file.id)} searchQuery={search}
                                                    onSelect={() => toggle(file.id)}
                                                    onPreview={() => setPreviewModal({ open: true, file })}
                                                    onRename={() => setRenameModal({ open: true, id: file.id, name: file.name })}
                                                    onCopyUrl={() => handleCopyUrl(file)}
                                                    onDelete={() => setConfirmModal({ open: true, id: file.id, name: file.name })}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Table view ── */}
                        {viewMode === "table" && (
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                                    <thead>
                                        <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                                            <th style={{ width: 44, padding: "11px 16px", textAlign: "center", borderBottom: "1px solid var(--border)" }}>
                                                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                                                    style={{ accentColor: "var(--or)", cursor: "pointer", width: 14, height: 14 }} />
                                            </th>
                                            <th style={{ width: 44, borderBottom: "1px solid var(--border)", padding: "11px 6px 11px 0" }} />
                                            {([
                                                { field: "name" as SortField, label: "File name", w: "auto" },
                                                { field: "type" as SortField, label: "Type", w: 110 },
                                                { field: "size" as SortField, label: "Size", w: 90 },
                                                { field: "createdAt" as SortField, label: "Uploaded", w: 100 },
                                            ]).map((col) => (
                                                <th key={col.field} className="af-th" onClick={() => toggleSort(col.field)} style={{
                                                    width: col.w, padding: "11px 14px", textAlign: "left",
                                                    borderBottom: "1px solid var(--border)",
                                                    fontSize: 11, fontWeight: 600,
                                                    color: sortField === col.field ? "var(--or)" : "var(--text3)",
                                                    textTransform: "uppercase", letterSpacing: "0.8px", whiteSpace: "nowrap",
                                                }}>
                                                    <span>{col.label}</span>
                                                    <SortIcon active={sortField === col.field} dir={sortDir} />
                                                </th>
                                            ))}
                                            <th style={{ width: 60, padding: "11px 14px", borderBottom: "1px solid var(--border)", fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Folder</th>
                                            <th style={{ width: 120, padding: "11px 14px", borderBottom: "1px solid var(--border)", fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageSlice.length === 0 ? (
                                            <tr><td colSpan={8} style={{ padding: "56px 24px", textAlign: "center", fontSize: 14, color: "var(--text3)" }}>No files found</td></tr>
                                        ) : pageSlice.map((file, idx) => (
                                            <tr key={file.id} className="af-row" style={{
                                                background: selected.has(file.id) ? "rgba(191,61,12,0.06)" : "transparent",
                                                borderBottom: idx < pageSlice.length - 1 ? "1px solid var(--border)" : "none",
                                                animation: `fadeSlideIn 0.2s ease ${idx * 0.025}s both`,
                                            }}>
                                                <td style={{ padding: "10px 16px", textAlign: "center" }}>
                                                    <input type="checkbox" checked={selected.has(file.id)} onChange={() => toggle(file.id)}
                                                        style={{ accentColor: "var(--or)", cursor: "pointer", width: 14, height: 14 }} />
                                                </td>
                                                <td style={{ padding: "10px 6px 10px 0" }}>
                                                    <FileThumbnail file={file} size={36} />
                                                </td>
                                                <td style={{ padding: "10px 14px" }}>
                                                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", marginBottom: 1, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {highlight(file.name, search)}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "monospace" }}>{file.id}</div>
                                                </td>
                                                <td style={{ padding: "10px 14px" }}><TypeBadge type={file.type} /></td>
                                                <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--text2)", whiteSpace: "nowrap" }}>{fmtSize(file.size)}</td>
                                                <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--text3)", whiteSpace: "nowrap" }}>{formatDate(file.createdAt)}</td>
                                                <td style={{ padding: "10px 14px" }}>
                                                    <span style={{ fontSize: 12, background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: 4, padding: "2px 7px", color: "var(--text3)" }}>{file.folder}</span>
                                                </td>
                                                <td style={{ padding: "10px 14px" }}>
                                                    <div style={{ display: "flex", gap: 4 }}>
                                                        <IconBtn title="Preview" onClick={() => setPreviewModal({ open: true, file })}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                                                            </svg>
                                                        </IconBtn>
                                                        <IconBtn title="Copy URL" onClick={() => handleCopyUrl(file)} accent>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                                                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                                            </svg>
                                                        </IconBtn>
                                                        <IconBtn title="Rename" onClick={() => setRenameModal({ open: true, id: file.id, name: file.name })}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                                            </svg>
                                                        </IconBtn>
                                                        <IconBtn title="Delete" danger onClick={() => setConfirmModal({ open: true, id: file.id, name: file.name })}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="3 6 5 6 21 6" />
                                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                                <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
                                                            </svg>
                                                        </IconBtn>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Pagination ── */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap", gap: 10,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 12, color: "var(--text3)" }}>Per page</span>
                                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{
                                    fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid var(--border)",
                                    background: "rgba(255,255,255,0.04)", color: "var(--text2)",
                                    cursor: "pointer", fontFamily: "'DM Sans',sans-serif", outline: "none",
                                }}>
                                    {PAGE_SIZES.map((n) => <option key={n} value={n} style={{ background: "#1a1917" }}>{n}</option>)}
                                </select>
                            </div>
                            <span style={{ fontSize: 12, color: "var(--text3)" }}>
                                {filtered.length === 0 ? "0" : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)}`} of {filtered.length}
                            </span>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                {[{ l: "«", a: () => setPage(1), d: safePage === 1 }, { l: "‹", a: () => setPage((p) => Math.max(1, p - 1)), d: safePage === 1 }].map((b) => (
                                    <button key={b.l} className="af-pg" onClick={b.a} disabled={b.d} style={{
                                        width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)",
                                        background: "transparent", color: b.d ? "var(--text3)" : "var(--text2)",
                                        cursor: b.d ? "not-allowed" : "pointer", fontSize: 14, opacity: b.d ? 0.4 : 1,
                                        display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
                                    }}>{b.l}</button>
                                ))}
                                {pageNums().map((n, i) =>
                                    n === "…" ? <span key={`e${i}`} style={{ width: 30, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>…</span> : (
                                        <button key={n} className="af-pg" onClick={() => setPage(n as number)} style={{
                                            width: 30, height: 30, borderRadius: 7,
                                            border: `1px solid ${safePage === n ? "rgba(191,61,12,0.35)" : "var(--border)"}`,
                                            background: safePage === n ? "var(--or-dim)" : "transparent",
                                            color: safePage === n ? "var(--or)" : "var(--text2)",
                                            cursor: "pointer", fontSize: 13, fontWeight: safePage === n ? 600 : 400,
                                            display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
                                        }}>{n}</button>
                                    )
                                )}
                                {[{ l: "›", a: () => setPage((p) => Math.min(totalPages, p + 1)), d: safePage === totalPages }, { l: "»", a: () => setPage(totalPages), d: safePage === totalPages }].map((b) => (
                                    <button key={b.l} className="af-pg" onClick={b.a} disabled={b.d} style={{
                                        width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)",
                                        background: "transparent", color: b.d ? "var(--text3)" : "var(--text2)",
                                        cursor: b.d ? "not-allowed" : "pointer", fontSize: 14, opacity: b.d ? 0.4 : 1,
                                        display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
                                    }}>{b.l}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}